import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Iplog.css';
import moment from 'moment';
import ReactTable from '../../components/ReactTable/ReactTable';
import { TIMELINE_CONST, ACTIVE_DEVICE } from '../../common/constant/common-constants';
import { getIpLogs } from '../../helper/IpLogHelper';
import { AssignHospital } from '../../helper/RequestCallbackHelper';
import { parseMachineRecord } from '../../utils/machinePayloadUtils';
import { ROLE } from '../../enum/role';
import { connect } from 'react-redux';
import HospitalDropdown from '../../components/HospitalDropdown/HospitalDropdown';

const INTERFACE_LABELS = {
  wlan0: 'Wi-Fi',
  wwan0: 'LTE',
  eth0: 'Ethernet',
  eth1: 'Ethernet',
};

const DEVICE_FILTER = {
  ALL: 'all',
  WIFI: 'wlan0',
  LTE: 'wwan0',
};

/** Only count Wi-Fi / LTE as active if the device reported within this window. */
const RECENT_ACTIVE_SEC = 60;

const isRecentlyUpdated = (updatedAt, withinSec = RECENT_ACTIVE_SEC) =>
  Boolean(updatedAt) && moment(updatedAt).isAfter(moment().subtract(withinSec, 'seconds'));

const getMetricValue = (metrics, aliases) => {
  for (const key of aliases) {
    if (metrics[key] != null && metrics[key] !== '') {
      return metrics[key];
    }
  }
  return null;
};

const getDiagnostics = (metrics) => ({
  latency: getMetricValue(metrics, ['latencyMs', 'latency', 'ping', 'pingMs']),
  packetLoss: getMetricValue(metrics, ['packetLossPct', 'packetLoss', 'packet_loss_pct', 'packetLossPercent']),
  signal: getMetricValue(metrics, ['signalDbm', 'signalDBM', 'signal_dBm', 'signal_dbm']),
  validated: getMetricValue(metrics, ['validated']),
});

const computePopupPosition = (anchorRect, popupWidth, popupHeight) => {
  const margin = 8;
  let top = anchorRect.bottom + margin;
  let left = anchorRect.left;

  if (left + popupWidth > window.innerWidth - margin) {
    left = window.innerWidth - popupWidth - margin;
  }
  if (top + popupHeight > window.innerHeight - margin) {
    top = anchorRect.top - popupHeight - margin;
  }

  return {
    top: Math.max(margin, top),
    left: Math.max(margin, left),
  };
};

const TriggerButton = ({ className, onOpen, onScheduleClose, onCancelClose, children, ...props }) => {
  const ref = useRef(null);

  return (
    <button
      ref={ref}
      type="button"
      className={`ip-popup-trigger ${className || ''}`}
      onMouseEnter={() => {
        onCancelClose();
        onOpen(ref.current, false);
      }}
      onMouseLeave={onScheduleClose}
      onClick={(event) => {
        event.stopPropagation();
        onCancelClose();
        onOpen(ref.current, true);
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const AnchoredPopup = ({ anchorRef, visible, onMouseEnter, onMouseLeave, children, className }) => {
  const popupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!visible || !anchorRef.current) return undefined;

    const updatePosition = () => {
      if (!anchorRef.current || !popupRef.current) return;
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const { offsetWidth, offsetHeight } = popupRef.current;
      setPosition(computePopupPosition(anchorRect, offsetWidth, offsetHeight));
    };

    updatePosition();
    const rafId = requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, anchorRef, children]);

  if (!visible) return null;

  return createPortal(
    <div
      ref={popupRef}
      className={className}
      style={{ top: position.top, left: position.left }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="tooltip"
    >
      {children}
    </div>,
    document.body
  );
};

const DiagnosticsPopupContent = ({ metrics }) => {
  const { latency, packetLoss, signal, validated } = getDiagnostics(metrics);
  const packetLossText =
    packetLoss == null ? '—' : String(packetLoss).includes('%') ? String(packetLoss) : `${packetLoss}%`;
  const isHealthyPacketLoss = packetLoss != null && Number(packetLoss) === 0;

  return (
    <>
      <div className="ip-diagnostics-header">
        <span className="ip-diagnostics-label">DIAGNOSTICS</span>
        {validated === true || validated === 'true' ? (
          <span className="ip-diagnostics-status-dot" aria-label="Validated" />
        ) : null}
      </div>
      <div className="ip-diagnostics-rows">
        <div className="ip-diagnostics-row">
          <span className="ip-diagnostics-key">Latency</span>
          <span className="ip-diagnostics-val">{latency != null ? `${latency}ms` : '—'}</span>
        </div>
        <div className="ip-diagnostics-row">
          <span className="ip-diagnostics-key">Packet Loss</span>
          <span className={`ip-diagnostics-val ${isHealthyPacketLoss ? 'ip-diagnostics-val--good' : ''}`}>
            {packetLossText}
          </span>
        </div>
        <div className="ip-diagnostics-row">
          <span className="ip-diagnostics-key">Signal</span>
          <span className="ip-diagnostics-val">{signal != null ? `${signal} dBm` : '—'}</span>
        </div>
      </div>
    </>
  );
};

const StatIconDevices = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7Zm8.94-2.06a1 1 0 0 0-.24-1.09l-1.08-1.08a1 1 0 0 0-1.09-.24 8.06 8.06 0 0 0-1.01-.41 1 1 0 0 0-1.15.57l-.45 1.04a1 1 0 0 0 .28 1.09 10.94 10.94 0 0 1 0 2.24 1 1 0 0 0-.28 1.09l.45 1.04a1 1 0 0 0 1.15.57 8.06 8.06 0 0 0 1.01-.41 1 1 0 0 0 1.09-.24l1.08-1.08a1 1 0 0 0 .24-1.09 10.94 10.94 0 0 0 0-2.24Z"
      fill="currentColor"
    />
  </svg>
);

const StatIconWifi = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm-4.24-2.76a6 6 0 0 1 8.48 0l1.06-1.06a7.5 7.5 0 0 0-10.6 0l1.06 1.06Zm-3.54-3.54a10.5 10.5 0 0 1 14.56 0l1.06-1.06a12 12 0 0 0-16.68 0l1.06 1.06Z"
      fill="currentColor"
    />
  </svg>
);

const StatIconLte = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 18h2v-4H4v4Zm4 0h2V8H8v10Zm4 0h2V4h-2v14Zm4 0h2v-7h-2v7Z" fill="currentColor" />
  </svg>
);

const StatIconRefresh = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M17.65 6.35A7.958 7.958 0 0 0 12 4V1L7 6l5 5V7c2.76 0 5 2.24 5 5 0 1.07-.34 2.07-.92 2.88l1.46 1.46A7.938 7.938 0 0 0 20 12c0-2.21-.9-4.21-2.35-5.65ZM6.35 17.65A7.938 7.938 0 0 0 12 20v3l5-5-5-5v3c-2.76 0-5-2.24-5-5 0-1.07.34-2.07.92-2.88L6.46 8.66A7.938 7.938 0 0 0 4 12c0 2.21.9 4.21 2.35 5.65Z"
      fill="currentColor"
    />
  </svg>
);

const stripIomtSuffix = (machineUDI) => {
  if (!machineUDI) return machineUDI;
  return String(machineUDI).replace(/-iomt$/i, '');
};

const formatMachineSerial = (machineUDI, activeDevice) => {
  if (!machineUDI) return '—';
  if (activeDevice === ACTIVE_DEVICE.V730i) {
    const match = machineUDI.split('-').filter((part) => part.includes('1G'));
    return match[0] || machineUDI;
  }
  if (activeDevice === ACTIVE_DEVICE.NX5) {
    return machineUDI.split('-').at(-1);
  }
  return machineUDI;
};

const getSerialLastFourNumber = (serial) => {
  if (!serial || serial === '—') return null;
  const lastFour = String(serial).slice(-4);
  const serialNumber = Number(lastFour);
  return Number.isFinite(serialNumber) ? serialNumber : null;
};

const matchesSerialSearch = (serial, query) => {
  if (!query) return true;
  const q = String(query).trim();
  if (!q) return true;

  const searchNumber = Number(q);
  if (!Number.isFinite(searchNumber)) return false;

  const serialNumber = getSerialLastFourNumber(serial);
  return serialNumber === searchNumber;
};

const isDeviceMatch = (machineUDI, activeDevice) => {
  if (!machineUDI) return false;
  const prefix = machineUDI.split('-')[0]?.[0];
  if (activeDevice === ACTIVE_DEVICE.V730i) return prefix === 'V';
  if (activeDevice === ACTIVE_DEVICE.NX5) return prefix === 'P';
  return true;
};

const normalizeSerial = (value) => String(value || '').trim().toLowerCase();

const matchesHospitalSerial = (item, hospitalFilter) => {
  if (!hospitalFilter) return true;
  const hospitalSerials = new Set(
    (hospitalFilter.serials || []).map(normalizeSerial).filter(Boolean)
  );
  if (!hospitalSerials.size) return false;

  const udi = normalizeSerial(item.machineUDI);
  if (!udi) return false;
  if (hospitalSerials.has(udi)) return true;

  const udiWithoutIomt = normalizeSerial(stripIomtSuffix(item.machineUDI));
  if (udiWithoutIomt && hospitalSerials.has(udiWithoutIomt)) return true;

  return udi.split('-').some((part) => part && hospitalSerials.has(part));
};

const normalizeIpLog = (record) => {
  const parsed = parseMachineRecord(record);
  if (!parsed) return null;

  if (parsed.legacy) {
    return {
      legacy: true,
      display: parsed.display,
      interfaces: {},
      interfaceMetrics: {},
      internetSpeed: null,
      activeInterface: null,
    };
  }

  return {
    legacy: false,
    interfaces: parsed.interfaces,
    interfaceMetrics: parsed.interfaceMetrics,
    internetSpeed: parsed.internetSpeed,
    activeInterface: parsed.activeInterface,
  };
};

const formatIpValue = (value) => {
  if (!value || value === 'N/A' || value === 'null' || value === 'undefined') {
    return null;
  }
  return value;
};

const getInterfaceIp = (normalized, iface, version) => {
  const data = normalized?.interfaces?.[iface];
  if (!data) return null;
  return formatIpValue(version === 4 ? (data.ipV4 ?? data.ipv4) : (data.ipV6 ?? data.ipv6));
};

const getInterfaceLatency = (normalized, iface) => {
  const metrics = normalized?.interfaceMetrics?.[iface] || {};
  return metrics.latencyMs ?? metrics.latency ?? metrics.ping ?? metrics.pingMs ?? null;
};

const copyToClipboard = (value) => {
  if (!value) return Promise.resolve();
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value);
  }

  return new Promise((resolve, reject) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

const CopyIpButton = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (event) => {
    event.stopPropagation();
    event.preventDefault();
    copyToClipboard(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    });
  };

  if (!value) return null;

  return (
    <button
      type="button"
      className={`ip-copy-btn${copied ? ' ip-copy-btn--copied' : ''}`}
      onClick={handleCopy}
      onMouseDown={(event) => event.stopPropagation()}
      title={copied ? 'Copied' : 'Copy IP'}
      aria-label={copied ? 'Copied' : `Copy ${value}`}
    >
      {copied ? (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3.5 8.5 6.5 11.5 12.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect x="5.5" y="5.5" width="8" height="8" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M3.5 10.5V3.5h7" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      )}
    </button>
  );
};

const NetworkCell = ({ normalized, iface, onOpen, onScheduleClose, onCancelClose }) => {
  const ipv4 = getInterfaceIp(normalized, iface, 4);
  const ipv6 = getInterfaceIp(normalized, iface, 6);
  const latency = getInterfaceLatency(normalized, iface);
  const isActive = normalized?.activeInterface === iface;
  const metrics = normalized?.interfaceMetrics?.[iface] || {};

  if (!ipv4 && !ipv6) {
    return <span className="ip-cell-empty">---</span>;
  }

  const openDiagnostics = (anchorEl, pinned) => {
    onOpen(anchorEl, pinned, {
      type: 'diagnostics',
      metrics,
    });
  };

  return (
    <div className={`ip-network-cell ${isActive ? 'ip-network-cell-active' : ''}`}>
      {ipv4 && (
        <div className="ip-network-line">
          <TriggerButton
            className="ip-network-address ip-network-address-btn"
            onOpen={openDiagnostics}
            onScheduleClose={onScheduleClose}
            onCancelClose={onCancelClose}
            title="View diagnostics"
          >
            {ipv4}
          </TriggerButton>
          <CopyIpButton value={ipv4} />
        </div>
      )}
      {ipv6 && (
        <div className="ip-network-line">
          {ipv4 ? (
            <span className="ip-network-address ip-network-address-v6" title={ipv6}>
              {ipv6}
            </span>
          ) : (
            <TriggerButton
              className="ip-network-address ip-network-address-btn ip-network-address-v6-btn"
              onOpen={openDiagnostics}
              onScheduleClose={onScheduleClose}
              onCancelClose={onCancelClose}
              title="View diagnostics"
            >
              {ipv6}
            </TriggerButton>
          )}
          <CopyIpButton value={ipv6} />
        </div>
      )}
      {latency != null && (
        <span className="ip-network-latency">
          <span className="ip-latency-dot" />
          {latency}ms
        </span>
      )}
    </div>
  );
};

const renderSpeedCell = (normalized) => {
  const speed = normalized?.internetSpeed;
  if (!speed || typeof speed !== 'object') return <span className="ip-cell-empty">---</span>;

  const download = speed.downloadMbps ?? speed.download ?? speed.down;
  const upload = speed.uploadMbps ?? speed.upload ?? speed.up;

  if (download == null && upload == null) {
    return <span className="ip-cell-empty">---</span>;
  }

  return (
    <div className="ip-speed-cell">
      {upload != null && (
        <div className="ip-speed-line">
          <span className="ip-speed-dir">↑</span> {upload} Mbps
        </div>
      )}
      {download != null && (
        <div className="ip-speed-line">
          <span className="ip-speed-dir">↓</span> {download} Mbps
        </div>
      )}
    </div>
  );
};

const renderEthernetCell = (normalized, popupHandlers) => {
  const hasEth1 =
    getInterfaceIp(normalized, 'eth1', 4) ||
    getInterfaceIp(normalized, 'eth1', 6) ||
    getInterfaceLatency(normalized, 'eth1') != null;
  return (
    <NetworkCell normalized={normalized} iface={hasEth1 ? 'eth1' : 'eth0'} {...popupHandlers} />
  );
};

const renderActiveInterface = (normalized) => {
  if (!normalized?.activeInterface) return <span className="ip-cell-empty">---</span>;
  const iface = normalized.activeInterface;
  return (
    <span className={`ip-active-pill ip-active-${iface}`}>
      {INTERFACE_LABELS[iface] || iface}
    </span>
  );
};

const IpLog = (props) => {
  const [logs, setLogs] = useState([]);
  const [machineSearch, setMachineSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [deviceFilter, setDeviceFilter] = useState(DEVICE_FILTER.ALL);
  const [hospitalFilter, setHospitalFilter] = useState(null);
  const [popup, setPopup] = useState(null);
  const searchInputRef = useRef(null);
  const anchorRef = useRef(null);
  const pinnedRef = useRef(false);
  const hideTimerRef = useRef(null);

  const openCMS = useCallback((machineUDI) => {
    if (!props.email || !machineUDI) return;
    AssignHospital({
      machineUDI: stripIomtSuffix(machineUDI),
      email: props.email,
      activeDevice: props.activeDevice,
    }).then((res) => {
      if (res === 'Hospital ID updated successfully.') {
        window.open(process.env.REACT_APP_CMS_APP_URL, '_blank');
      } else {
        alert(res);
      }
    });
  }, [props.email, props.activeDevice]);

  const closePopup = useCallback(() => {
    pinnedRef.current = false;
    setPopup(null);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(hideTimerRef.current);
  }, []);

  const scheduleClose = useCallback(() => {
    if (pinnedRef.current) return;
    hideTimerRef.current = setTimeout(() => setPopup(null), 120);
  }, []);

  const openPopup = useCallback((anchorEl, pinned, data) => {
    if (!anchorEl) return;
    clearTimeout(hideTimerRef.current);
    anchorRef.current = anchorEl;
    pinnedRef.current = pinned;
    setPopup({ ...data, pinned });
  }, []);

  const fetchLogs = useCallback((isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    getIpLogs()
      .then((res) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        setLogs(data);
      })
      .catch(() => {
        setError('Unable to load IP logs. Please try again.');
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  useEffect(() => {
    fetchLogs();

    const handleKeyDown = (event) => {
      if (event.metaKey && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [fetchLogs]);

  useEffect(() => {
    setDeviceFilter(DEVICE_FILTER.ALL);
    setHospitalFilter(null);
  }, [props.activeDevice]);

  const handleStatCardClick = useCallback((filter) => {
    setDeviceFilter(filter);
    fetchLogs(true);
  }, [fetchLogs]);

  useEffect(() => {
    if (!popup) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [popup, closePopup]);

  useEffect(() => {
    if (!popup?.pinned) return undefined;

    const handleClickOutside = (event) => {
      if (
        !event.target.closest('.ip-diagnostics-popup') &&
        !event.target.closest('.ip-popup-trigger')
      ) {
        closePopup();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popup?.pinned, closePopup]);

  const popupHandlers = useMemo(
    () => ({
      onOpen: openPopup,
      onScheduleClose: scheduleClose,
      onCancelClose: cancelClose,
    }),
    [openPopup, scheduleClose, cancelClose]
  );

  const tableData = useMemo(() => {
    return logs
      .filter((item) => isDeviceMatch(item.machineUDI, props.activeDevice))
      .filter((item) => matchesHospitalSerial(item, hospitalFilter))
      .map((item) => ({
        ...item,
        normalized: normalizeIpLog(item),
        serial: formatMachineSerial(item.machineUDI, props.activeDevice),
      }))
      .filter((item) => {
        if (deviceFilter !== DEVICE_FILTER.ALL) {
          return (
            item.normalized?.activeInterface === deviceFilter &&
            isRecentlyUpdated(item.updatedAt)
          );
        }
        return true;
      })
      .filter((item) => matchesSerialSearch(item.serial, machineSearch))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [logs, props.activeDevice, machineSearch, deviceFilter, hospitalFilter]);

  const stats = useMemo(() => {
    const deviceLogs = logs.filter((item) => {
      if (!isDeviceMatch(item.machineUDI, props.activeDevice)) return false;
      if (!matchesHospitalSerial(item, hospitalFilter)) return false;
      const serial = formatMachineSerial(item.machineUDI, props.activeDevice);
      return matchesSerialSearch(serial, machineSearch);
    });

    const withData = deviceLogs
      .map((item) => {
        const normalized = normalizeIpLog(item);
        if (!normalized) return null;
        return { ...normalized, updatedAt: item.updatedAt };
      })
      .filter(Boolean);

    return {
      total: withData.length,
      wifiActive: withData.filter(
        (n) => n.activeInterface === 'wlan0' && isRecentlyUpdated(n.updatedAt)
      ).length,
      lteActive: withData.filter(
        (n) => n.activeInterface === 'wwan0' && isRecentlyUpdated(n.updatedAt)
      ).length,
      recent: deviceLogs.filter((item) =>
        moment(item.updatedAt).isAfter(moment().subtract(24, 'hours'))
      ).length,
    };
  }, [logs, props.activeDevice, hospitalFilter, machineSearch]);

  const columns = useMemo(
    () => [
      {
        title: 'SR. NO.',
        dataIndex: 'serial',
        key: 'serial',
        className: 'ip-logs-serial-col header_column text-align-left',
        width: '7%',
        render: (value, row) => (
          <TriggerButton
            className="ip-serial-value ip-serial-value-btn"
            onOpen={(anchorEl, pinned) =>
              openPopup(anchorEl, pinned, {
                type: 'serial',
                machineUDI: row.machineUDI,
                serial: value,
              })
            }
            onScheduleClose={scheduleClose}
            onCancelClose={cancelClose}
            title="View full serial number"
          >
            {value}
          </TriggerButton>
        ),
      },
      {
        title: 'ACTIVE',
        dataIndex: 'normalized',
        key: 'activeInterface',
        className: 'header_column ip-active-col',
        width: '8%',
        render: (_, row) => renderActiveInterface(row.normalized),
      },
      {
        title: 'NETWORK CONNECTIVITY',
        className: 'ip-logs-group-header header_column',
        children: [
          {
            title: 'WI-FI',
            key: 'wlan0',
            className: 'header_column ip-network-col',
            width: '19%',
            render: (_, row) => {
              if (row.normalized?.legacy) {
                const legacyIp = row.normalized.display;
                if (!legacyIp) return <span className="ip-cell-empty">---</span>;
                return (
                  <div className="ip-network-line">
                    <TriggerButton
                      className="ip-network-address ip-network-address-btn"
                      onOpen={(anchorEl, pinned) =>
                        openPopup(anchorEl, pinned, {
                          type: 'diagnostics',
                          metrics: row.normalized?.interfaceMetrics?.wlan0 || {},
                        })
                      }
                      onScheduleClose={scheduleClose}
                      onCancelClose={cancelClose}
                      title="View diagnostics"
                    >
                      {legacyIp}
                    </TriggerButton>
                    <CopyIpButton value={legacyIp} />
                  </div>
                );
              }
              return <NetworkCell normalized={row.normalized} iface="wlan0" {...popupHandlers} />;
            },
          },
          {
            title: 'LTE',
            key: 'wwan0',
            className: 'header_column ip-network-col',
            width: '19%',
            render: (_, row) => <NetworkCell normalized={row.normalized} iface="wwan0" {...popupHandlers} />,
          },
          {
            title: 'ETHERNET',
            key: 'eth1',
            className: 'header_column ip-network-col',
            width: '19%',
            render: (_, row) => renderEthernetCell(row.normalized, popupHandlers),
          },
        ],
      },
      {
        title: 'INTERNET SPEED',
        dataIndex: 'normalized',
        key: 'internetSpeed',
        className: 'header_column ip-speed-col',
        width: '13%',
        render: (_, row) => renderSpeedCell(row.normalized),
      },
      {
        title: 'LAST UPDATED',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        className: 'header_column ip-updated-col',
        width: '13%',
        render: (value) =>
          value ? (
            <div className="ip-updated-cell">
              <span className="ip-updated-date">{moment(value).format(TIMELINE_CONST.DATE_FORMAT)}</span>
              <span className="ip-updated-time">{moment(value).format(TIMELINE_CONST.TIME_FORMAT)}</span>
            </div>
          ) : (
            <span className="ip-cell-empty">---</span>
          ),
      },
      {
        title: 'ACTIONS',
        key: 'actions',
        className: 'header_column ip-actions-col',
        width: '10%',
        render: (_, row) => (
          <button
            type="button"
            className="ip-cms-btn"
            onClick={() => openCMS(row.machineUDI)}
          >
            Open CMS
          </button>
        ),
      },
    ],
    [openPopup, scheduleClose, cancelClose, popupHandlers, openCMS]
  );

  const machinePageTitle = useMemo(
    () => (props.activeDevice === ACTIVE_DEVICE.NX5
      ? `${ACTIVE_DEVICE.NX5} Network Monitor`
      : `${ACTIVE_DEVICE.V730i} Network Monitor`),
    [props.activeDevice],
  );

  if (!(props.userRole === ROLE.ADMIN || props.userRole === ROLE.PRODUCTION)) {
    return (
      <div className="ip-logs-unauthorized">
        <h1>Unauthorized Access</h1>
        <p>You do not have the necessary permissions to access this page.</p>
      </div>
    );
  }

  return (
    <div className="ip-logs-page">
      <div className="ots-details-container">
        <header className="ip-logs-header">
          <h3 className="ip-logs-title">{machinePageTitle}</h3>
          <div className="ip-logs-header-actions">
            <HospitalDropdown
              className="ip-logs-hospital-dropdown"
              value={hospitalFilter?.CustomerNo || ''}
              onChange={(hospital) => {
                if (!hospital) {
                  setHospitalFilter(null);
                  return;
                }
                setHospitalFilter({
                  CustomerNo: hospital.CustomerNo,
                  Name: hospital.Name,
                  serials: (hospital.machines || [])
                    .map((machine) => machine?.SerialNo)
                    .filter(Boolean),
                });
                setDeviceFilter(DEVICE_FILTER.ALL);
              }}
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              aria-label="Search last 4 digits"
              placeholder="Search last 4 digits..."
              value={machineSearch}
              onChange={(event) => {
                const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 4);
                setMachineSearch(digitsOnly);
              }}
              className="ip-logs-search-input"
              ref={searchInputRef}
            />
          </div>
        </header>

        <section className="ip-logs-stats-grid">
          <button
            type="button"
            className={`ip-stat-card ip-stat-card--devices ${deviceFilter === DEVICE_FILTER.ALL ? 'ip-stat-card--active' : ''}`}
            onClick={() => handleStatCardClick(DEVICE_FILTER.ALL)}
            aria-pressed={deviceFilter === DEVICE_FILTER.ALL}
          >
            <div className="ip-stat-card-content">
              <span className="ip-stat-label">TOTAL MACHINES</span>
              <span className="ip-stat-value">{stats.total}</span>
            </div>
            <div className="ip-stat-icon ip-stat-icon--devices">
              <StatIconDevices />
            </div>
          </button>

          <button
            type="button"
            className={`ip-stat-card ip-stat-card--wifi ${deviceFilter === DEVICE_FILTER.WIFI ? 'ip-stat-card--active' : ''}`}
            onClick={() => handleStatCardClick(DEVICE_FILTER.WIFI)}
            aria-pressed={deviceFilter === DEVICE_FILTER.WIFI}
          >
            <div className="ip-stat-card-content">
              <span className="ip-stat-label">ACTIVE WI-FI</span>
              <div className="ip-stat-value-row">
                <span className="ip-stat-value">{stats.wifiActive}</span>
                <span className="ip-stat-badge ip-stat-badge--online">ONLINE</span>
              </div>
            </div>
            <div className="ip-stat-icon ip-stat-icon--wifi">
              <StatIconWifi />
            </div>
          </button>

          <button
            type="button"
            className={`ip-stat-card ip-stat-card--lte ${deviceFilter === DEVICE_FILTER.LTE ? 'ip-stat-card--active' : ''}`}
            onClick={() => handleStatCardClick(DEVICE_FILTER.LTE)}
            aria-pressed={deviceFilter === DEVICE_FILTER.LTE}
          >
            <div className="ip-stat-card-content">
              <span className="ip-stat-label">ACTIVE LTE</span>
              <div className="ip-stat-value-row">
                <span className="ip-stat-value">{stats.lteActive}</span>
                <span className="ip-stat-badge ip-stat-badge--connected">CONNECTED</span>
              </div>
            </div>
            <div className="ip-stat-icon ip-stat-icon--lte">
              <StatIconLte />
            </div>
          </button>

          <button
            type="button"
            className="ip-stat-card ip-stat-card--recent"
            onClick={() => fetchLogs(true)}
            disabled={refreshing}
            aria-label="Refresh IP logs"
          >
            <div className="ip-stat-card-content">
              <span className="ip-stat-label">UPDATED (24H)</span>
              <span className="ip-stat-value">{stats.recent}</span>
            </div>
            <div className={`ip-stat-icon ip-stat-icon--recent ${refreshing ? 'ip-stat-icon--spinning' : ''}`}>
              <StatIconRefresh />
            </div>
          </button>
        </section>

        <div className="ip-logs-body">
        {loading && (
          <div className="ip-logs-state">
            <div className="ip-logs-spinner" />
            <p>Loading IP logs…</p>
          </div>
        )}

        {!loading && error && (
          <div className="ip-logs-state ip-logs-state-error">
            <p>{error}</p>
            <button type="button" onClick={() => fetchLogs()}>Try again</button>
          </div>
        )}

        {!loading && !error && tableData.length === 0 && (
          <div className="ip-logs-state">
            <p>
              {machineSearch.trim()
                ? 'No machines match these last 4 digits.'
                : hospitalFilter
                  ? 'No machines match this hospital.'
                  : deviceFilter === DEVICE_FILTER.WIFI
                    ? 'No active Wi-Fi machines found.'
                    : deviceFilter === DEVICE_FILTER.LTE
                      ? 'No active LTE machines found.'
                      : 'No IP logs available for this machine type.'}
            </p>
          </div>
        )}

        {!loading && !error && tableData.length > 0 && (
          <div className="ip-logs-table-panel">
            <div className="ip-logs-table-wrap">
              <ReactTable
                columns={columns}
                data={tableData}
                rowKey="machineUDI"
              />
            </div>
          </div>
        )}
        </div>
      </div>

      <AnchoredPopup
        anchorRef={anchorRef}
        visible={Boolean(popup)}
        className={`ip-diagnostics-popup ${popup?.type === 'serial' ? 'ip-diagnostics-popup--serial' : ''}`}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        {popup?.type === 'serial' ? (
          <>
            <div className="ip-diagnostics-header">
              <span className="ip-diagnostics-label">SERIAL NUMBER</span>
            </div>
            <p className="ip-serial-full">{stripIomtSuffix(popup.machineUDI)}</p>
          </>
        ) : popup?.type === 'diagnostics' ? (
          <DiagnosticsPopupContent metrics={popup.metrics || {}} />
        ) : null}
      </AnchoredPopup>
    </div>
  );
};

const mapStateToProps = (state) => ({
  email: state.userDetails?.email,
  userRole: state.userDetails.userRole,
  activeDevice: state.setactiveDevice.device,
});

export default connect(mapStateToProps)(IpLog);
